<script setup lang="ts">
import { useId } from "vue";

// The multi-line twin of HPixTextField: same model, same events, a native <textarea>. Optional
// props accept an explicit `undefined` for consumers that type-check with
// `exactOptionalPropertyTypes` (see HPixTextField); Vue treats a passed `undefined` as absent.
type HPixTextAreaProps = {
  modelValue?: string | undefined;
  id?: string | undefined;
  placeholder?: string | undefined;
  disabled?: boolean;
  required?: boolean;
  name?: string | undefined;
  autocomplete?: string | undefined;
  /** Visible lines; the native default (2) is too short for a message. */
  rows?: number;
};

type HPixTextAreaEmits = {
  "update:modelValue": [value: string];
  blur: [];
};

const props = withDefaults(defineProps<HPixTextAreaProps>(), {
  modelValue: "",
  disabled: false,
  required: false,
  rows: 4,
});

const emits = defineEmits<HPixTextAreaEmits>();

const generatedId = useId();
const textAreaId = () => props.id ?? generatedId;

const handleInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement;
  emits("update:modelValue", target.value);
};
</script>

<template>
  <textarea
    :id="textAreaId()"
    class="h-pix-text-area"
    :value="props.modelValue"
    :rows="props.rows"
    :placeholder="props.placeholder"
    :disabled="props.disabled"
    :aria-disabled="props.disabled || undefined"
    :required="props.required || undefined"
    :name="props.name"
    :autocomplete="props.autocomplete"
    @input="handleInput"
    @blur="emits('blur')"
  ></textarea>
</template>
